import { useQuery } from "@apollo/client";
import { Dialog, Transition } from "@headlessui/react";
import * as Xstate from "@xstate/react";
import _ from "lodash";
import React, { Fragment, useMemo, useState } from "react";
import { LuUsers } from "react-icons/lu";
import { useEffectOnce } from "usehooks-ts";

import { GlobalContext } from "../../app/stateMachines/GlobalContext";
import { LottieLoading } from "../../components/graphics/LottieLoading";
import {
  GetUserInfoByEmail,
  GetUserInfoByEmailVariables,
} from "../../generated/operation-result-types";
import { GET_USER_INFO_BY_EMAIL_GQL } from "../../queries/UserQueries.gql";
import { useAppDispatch, useAppSelector } from "../../redux/redux";
import { getUser } from "../../services/AppService";
import { getLinkedFirm } from "../../sessionStorage/sessionStorage";
import { useGetRolesQuery } from "../../slices/RoleSlice";
import { useEditUserMutation, useGetUsersQuery } from "../../slices/UserSlice";
import { GetPageTitle } from "../../support/ScrollToTop";
import {
  ColorPicker,
  DEFAULT_AVATAR_COLOR,
} from "../settings/team/editUserParts";
import { TwoStepProfileCard } from "../twoStep/TwoStepProfileCard";
import EditUserProfile from "./EditUserProfile";
import { myRoles } from "./profile.utils";

const CARD_CLASSES =
  "mb-5 rounded-xl border border-[#E7E9EF] bg-white shadow-[0_1px_2px_rgba(20,22,34,0.04)]";
const CARD_TITLE_CLASSES = "text-base font-extrabold text-[#1F2433]";

export const UserProfileView: React.FC<unknown> = () => {
  useEffectOnce(() => {
    document.title = GetPageTitle("User profile");
  });

  // xstate
  const { userInfoService } = React.useContext(GlobalContext);
  const [userInfoState] = Xstate.useActor(userInfoService);
  const { userInfoByEmail } = userInfoState.context;
  const [isEdit, setIsEdit] = useState(false);
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.appReducer);
  const orgId = getLinkedFirm()?.orgId || "";
  const { data: teammates = [] } = useGetUsersQuery(orgId, { skip: !orgId });
  const { data: roles = [] } = useGetRolesQuery({ orgId }, { skip: !orgId });
  const [editUser, { isError: isColorError }] = useEditUserMutation();
  const me = teammates.find((teammate) => teammate.id === user?.id);
  const [pickedColor, setPickedColor] = useState<string | null>(null);
  const avatarColor =
    pickedColor ||
    me?.userProfile?.color ||
    user?.userProfile?.color ||
    DEFAULT_AVATAR_COLOR;
  const takenColors = useMemo(
    () =>
      teammates
        .filter((teammate) => teammate.id !== user?.id)
        .map((teammate) => teammate.userProfile?.color)
        .filter(Boolean) as string[],
    [teammates, user?.id],
  );
  const rolesList = useMemo(() => myRoles(roles, user?.id), [roles, user?.id]);

  // Same save as Settings > Team > Edit user: the whole user with a new color.
  const changeColor = async (color: string) => {
    if (!me) return;
    setPickedColor(color);
    try {
      await editUser({ ...me, color }).unwrap();
      dispatch(getUser({ userId: me.id, orgId }));
    } catch {
      setPickedColor(null);
    }
  };

  /*const [selectedTimezone, setSelectedTimezone] = useState<ITimezone>(
    Intl.DateTimeFormat().resolvedOptions().timeZone,
  );*/

  const closeEdit = () => {
    setIsEdit(false);
  };
  const openEdit = () => {
    setIsEdit(true);
  };
  const {
    loading: loadingQ,
    error: errorQ,
    data: dataQ,
    refetch,
  } = useQuery<GetUserInfoByEmail, GetUserInfoByEmailVariables>(
    GET_USER_INFO_BY_EMAIL_GQL,
    {
      variables: {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        email: userInfoByEmail!.userProfile!.email,
      },
    },
  );

  if (loadingQ) {
    return <LottieLoading />;
  }
  if (errorQ) {
    console.error("UserProfileView", { errorQ });
    throw new Error("Error getting UserProfileView");
  }

  const userResponse = _.first(dataQ?.users?.edges)?.node;
  if (!userResponse) {
    throw new Error("Missing user in UserProfileView");
  }

  const editUserProfile = (
    <>
      <button
        onClick={openEdit}
        type={"button"}
        className={
          "h-[34px] shrink-0 rounded-lg border border-[#DDE0E7] bg-white px-4 text-[13px] font-bold text-[#4A5163] transition-colors hover:border-[#6B4EFF] hover:text-[#6B4EFF]"
        }>
        Edit
      </button>
      <Transition appear show={isEdit} as={Fragment}>
        <Dialog as={"div"} className={"relative z-10"} onClose={closeEdit}>
          <Transition.Child
            as={Fragment}
            enter={"ease-out duration-300"}
            enterFrom={"opacity-0"}
            enterTo={"opacity-100"}
            leave={"ease-in duration-200"}
            leaveFrom={"opacity-100"}
            leaveTo={"opacity-0"}>
            <div className={"fixed inset-0 bg-black bg-opacity-25"} />
          </Transition.Child>

          <div className={"fixed inset-0 overflow-y-auto"}>
            <div
              className={
                "flex min-h-full items-center justify-center p-4 text-center"
              }>
              <Transition.Child
                as={Fragment}
                enter={"ease-out duration-300"}
                enterFrom={"opacity-0 scale-95"}
                enterTo={"opacity-100 scale-100"}
                leave={"ease-in duration-200"}
                leaveFrom={"opacity-100 scale-100"}
                leaveTo={"opacity-0 scale-95"}>
                <Dialog.Panel
                  className={
                    "z-index-100 w-full max-w-lg transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all"
                  }>
                  <div>
                    <EditUserProfile
                      closeModal={closeEdit}
                      id={userResponse.userProfile?.id}
                      refetch={refetch}
                    />
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
  const fullName = userResponse.userProfile?.fullName || "";
  const email = userResponse.userProfile?.email || "";
  const initials = fullName
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");

  return (
    <div className={"max-w-[900px] font-Manrope"}>
      <div className={`${CARD_CLASSES} px-[26px] py-6`}>
        <div className={"mb-[22px] flex items-start justify-between gap-4"}>
          <span className={CARD_TITLE_CLASSES}>Profile</span>
          {editUserProfile}
        </div>

        <div
          className={
            "mb-1.5 flex items-center gap-[15px] rounded-xl border border-[#ECEFF3] bg-[#FAFBFC] px-[18px] py-4"
          }>
          <span
            className={
              "flex h-[52px] w-[52px] shrink-0 items-center justify-center rounded-full text-lg font-bold text-white"
            }
            style={{ backgroundColor: avatarColor }}>
            {initials}
          </span>
          <span className={"flex min-w-0 flex-1 flex-col gap-0.5"}>
            <span className={"text-base font-extrabold text-[#1F2433]"}>
              {fullName}
            </span>
            <span className={"truncate text-[13px] text-[#8A90A2]"}>
              {email}
            </span>
          </span>
          {me && (
            <ColorPicker
              value={avatarColor}
              takenColors={takenColors}
              onChange={changeColor}
              label={"Avatar color"}
            />
          )}
        </div>
        {isColorError && (
          <div className={"mt-2 text-[12.5px] font-semibold text-[#D14343]"}>
            Couldn&apos;t save the avatar color. Please try again.
          </div>
        )}

        <div className={"mt-[18px]"}>
          <div
            className={
              "flex items-center justify-between gap-4 border-b border-[#F1F2F5] px-0.5 py-[13px]"
            }>
            <span className={"text-[13px] font-semibold text-[#8A90A2]"}>
              Name
            </span>
            <span className={"text-sm font-bold text-[#1F2433]"}>
              {fullName}
            </span>
          </div>
          <div
            className={
              "flex items-center justify-between gap-4 px-0.5 py-[13px]"
            }>
            <span className={"text-[13px] font-semibold text-[#8A90A2]"}>
              Email
            </span>
            <span
              className={"min-w-0 truncate text-sm font-bold text-[#1F2433]"}>
              {email}
            </span>
          </div>
        </div>
      </div>

      <div className={`${CARD_CLASSES} px-[26px] py-[22px]`}>
        <div className={"mb-1"}>
          <span className={CARD_TITLE_CLASSES}>My roles</span>
        </div>
        <span className={"mb-4 block text-[13px] text-[#8A90A2]"}>
          Roles you belong to. Work assigned to a role waits in Queued until
          someone picks it up.
        </span>
        {rolesList.length ? (
          <div className={"flex flex-wrap gap-2.5"}>
            {rolesList.map((role) => (
              <span
                key={role.id}
                className={
                  "inline-flex items-center gap-2 rounded-full border border-[#ECEFF3] bg-[#FAFBFC] py-2 pl-2.5 pr-3.5"
                }>
                <span
                  className={"h-[9px] w-[9px] shrink-0 rounded-full"}
                  style={{ backgroundColor: role.color }}
                />
                <span className={"text-[13.5px] font-bold text-[#2B3242]"}>
                  {role.name}
                </span>
              </span>
            ))}
          </div>
        ) : (
          <div
            className={
              "flex flex-col items-center gap-2.5 rounded-[11px] border border-dashed border-[#DFE3EA] bg-[#FAFBFC] px-5 py-[26px] text-center"
            }>
            <span
              className={
                "flex h-10 w-10 items-center justify-center rounded-full bg-[#F1EEFC] text-[#6B4EFF]"
              }>
              <LuUsers size={20} strokeWidth={1.8} />
            </span>
            <div className={"text-sm font-bold text-[#3A4256]"}>
              You&apos;re not in any roles yet
            </div>
            <div
              className={
                "max-w-[320px] text-[12.5px] leading-[1.5] text-[#8A90A2]"
              }>
              An admin can add you to a role in Settings → Roles. Once
              you&apos;re in a role, work assigned to it will appear in your
              Queued list.
            </div>
          </div>
        )}
      </div>

      <TwoStepProfileCard email={user?.userProfile?.email || ""} />

      <div className={`${CARD_CLASSES} mb-0 px-[26px] py-[22px]`}>
        <span className={`${CARD_TITLE_CLASSES} mb-2 block`}>
          Change password
        </span>
        <span className={"mb-3.5 block text-[13.5px] text-[#5F6577]"}>
          Follow these steps to change your password:
        </span>
        <ol
          className={
            "flex list-decimal flex-col gap-2 pl-5 text-[13.5px] leading-[1.5] text-[#4A5163]"
          }>
          <li>Sign out of ProCharted</li>
          <li>Use the &quot;Forgot my password&quot; link to reset it</li>
        </ol>
      </div>
    </div>
  );
};
